import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Chip,
  Alert,
  Button,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import { useNavigate } from 'react-router-dom';

export default function SuggestedTopicsMetrics({ suggestedTopics }) {
  const navigate = useNavigate();

  if (!suggestedTopics || suggestedTopics.length === 0) {
    return null; // Don't show the card if there are no suggestions
  }

  const totalSuggestions = suggestedTopics.reduce(
    (sum, course) => sum + course.segments_needing_topics,
    0
  );

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <LightbulbIcon sx={{ mr: 1, color: 'warning.main' }} />
          <Typography variant="h6">
            Suggested Topics
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          {totalSuggestions} segments across {suggestedTopics.length} course{suggestedTopics.length !== 1 ? 's' : ''} need topic assignments
        </Alert>

        <List dense>
          {suggestedTopics.map((course) => {
            // Determine health color based on percentage
            const getHealthColor = (percentage) => {
              if (percentage >= 90) return 'success';
              if (percentage >= 70) return 'warning';
              return 'error';
            };

            const healthColor = getHealthColor(course.topic_health_percentage);

            return (
              <ListItem 
                key={course.course_id}
                sx={{ 
                  borderRadius: 1,
                  mb: 1,
                  bgcolor: 'background.default',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  }
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2">
                          {course.course_name}
                        </Typography>
                        <Tooltip title={`Topic Health: ${course.topic_health_percentage}% of categorized segments have sufficient topics`}>
                          <Chip
                            icon={<HealthAndSafetyIcon />}
                            label={`${course.topic_health_percentage}%`}
                            size="small"
                            color={healthColor}
                            variant="outlined"
                          />
                        </Tooltip>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Chip 
                          label={`${course.suggested_topics_count} topics`}
                          size="small"
                          color="secondary"
                        />
                        <Chip 
                          label={`${course.segments_needing_topics}/${course.total_categorized_segments}`}
                          size="small"
                          color="warning"
                        />
                      </Box>
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 1 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={course.topic_health_percentage} 
                        color={healthColor}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {course.segments_needing_topics} of {course.total_categorized_segments} categorized segments need topic assignments
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            );
          })}
        </List>

        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => navigate('/courses')}
            fullWidth
          >
            Go to Course Management
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}